/**
 * AddressAutocomplete — address input with live typeahead suggestions backed
 * by our provider-agnostic `/geo/autocomplete` endpoint. The backend uses
 * Google Places (New) as the primary source when the Google Maps integration
 * has a server key, and falls back to OpenStreetMap Nominatim otherwise.
 * Google predictions have coordinates resolved on selection via
 * `/geo/place-details`; OSM predictions include coordinates inline.
 *
 * Two modes (auto-detected from the `value` prop type):
 *   • String mode — value/onChange are strings; onResolve gets the full place.
 *   • Structured mode — value is a StructuredAddress record.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { api } from '@/lib/api';
import { MapPin, Loader2 } from 'lucide-react';

export interface StructuredAddress {
  formatted: string;
  place_id?: string;
  lat?: number | null;
  lng?: number | null;
  suburb?: string;
  city?: string;
  province?: string;
  postal_code?: string;
  country?: string;
}

interface Suggestion {
  formatted: string;
  lat: number | null;
  lng: number | null;
  place_id?: string;
  source?: string;
  suburb?: string;
  city?: string;
  province?: string;
  postal_code?: string;
  country?: string;
}

interface CommonProps {
  placeholder?: string;
  disabled?: boolean;
  testid?: string;
  className?: string;
  countries?: string[];
}
interface StringProps extends CommonProps {
  value: string;
  onChange: (v: string) => void;
  onResolve?: (p: { formatted_address: string; lat: number; lng: number; place_id?: string; city?: string; province?: string; postal_code?: string; country?: string }) => void;
}
interface StructuredProps extends CommonProps {
  value: StructuredAddress | null;
  onChange: (v: StructuredAddress | null) => void;
  onResolve?: never;
}
type Props = StringProps | StructuredProps;

function isStructured(v: unknown): v is StructuredAddress | null {
  return v === null || (typeof v === 'object' && v !== null && 'formatted' in v);
}

export const AddressAutocomplete: React.FC<Props> = (props) => {
  const { placeholder = 'Start typing an address…', disabled, testid, className, countries } = props;
  const structured = isStructured(props.value);
  const displayValue: string = structured
    ? (props.value as StructuredAddress | null)?.formatted ?? ''
    : (props.value as string);

  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const boxRef = useRef<HTMLDivElement | null>(null);
  const skipNextFetch = useRef(false);

  const country = (countries && countries[0]) || 'za';

  // Close the dropdown on outside click.
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const fetchSuggestions = (q: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (q.trim().length < 3) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await api.get<{ suggestions: Suggestion[] }>('/geo/autocomplete', {
          params: { q, country: country.toLowerCase() },
        });
        setSuggestions(r.data.suggestions || []);
        setOpen((r.data.suggestions || []).length > 0);
      } catch {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 350);
  };

  const onTyped = (v: string): void => {
    if (structured) {
      const current = (props.value as StructuredAddress | null) ?? null;
      (props as StructuredProps).onChange(current ? { ...current, formatted: v } : { formatted: v });
    } else {
      (props as StringProps).onChange(v);
    }
    if (skipNextFetch.current) {
      skipNextFetch.current = false;
      return;
    }
    fetchSuggestions(v);
  };

  const pick = async (s: Suggestion): Promise<void> => {
    skipNextFetch.current = true;
    setOpen(false);
    setSuggestions([]);
    // Google predictions carry no coordinates — resolve them on selection via
    // Place Details. OSM predictions already include lat/lng.
    let resolved = s;
    if (s.source === 'google' && (s.lat == null || s.lng == null) && s.place_id) {
      try {
        const r = await api.get<Suggestion>('/geo/place-details', { params: { place_id: s.place_id } });
        resolved = { ...s, ...r.data };
      } catch {
        resolved = s;
      }
    }
    if (structured) {
      (props as StructuredProps).onChange({
        formatted: resolved.formatted, place_id: resolved.place_id, lat: resolved.lat, lng: resolved.lng,
        suburb: resolved.suburb, city: resolved.city, province: resolved.province,
        postal_code: resolved.postal_code, country: resolved.country,
      });
    } else {
      (props as StringProps).onChange(resolved.formatted);
      (props as StringProps).onResolve?.({
        formatted_address: resolved.formatted, lat: resolved.lat as number, lng: resolved.lng as number, place_id: resolved.place_id,
        city: resolved.city, province: resolved.province, postal_code: resolved.postal_code, country: resolved.country,
      });
    }
  };

  return (
    <div className={`relative ${className || ''}`} ref={boxRef}>
      <Input
        value={displayValue}
        onChange={(e) => onTyped(e.target.value)}
        onFocus={() => { if (suggestions.length) setOpen(true); }}
        placeholder={placeholder}
        disabled={disabled}
        data-testid={testid}
        className="pr-8"
        autoComplete="off"
      />
      <div className="absolute right-2 top-1/2 -translate-y-1/2 text-raptor-navy/40 pointer-events-none">
        {loading ? <Loader2 size={14} className="animate-spin" /> : <MapPin size={14} className="text-raptor-blue" />}
      </div>

      {open && suggestions.length > 0 && (
        <div
          className="absolute z-50 mt-1 w-full rounded-md border border-border bg-white shadow-lg max-h-64 overflow-y-auto"
          data-testid={testid ? `${testid}-suggestions` : 'address-suggestions'}
        >
          {suggestions.map((s, i) => (
            <button
              key={`${s.place_id}-${i}`}
              type="button"
              onClick={() => pick(s)}
              data-testid={`address-suggestion-${i}`}
              className="w-full text-left px-3 py-2 text-sm font-body hover:bg-raptor-navy/5 flex items-start gap-2 border-b border-border/40 last:border-0"
            >
              <MapPin size={13} className="mt-0.5 text-raptor-blue shrink-0" />
              <span className="text-raptor-navy">{s.formatted}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;

